import { t } from 'i18next';
import z from 'zod';
import { idString } from '../../lib/schemas/id-string';
import { optionalNumericString } from '../../lib/schemas/numeric-string';

export const COMPANY_NET_TERMS = [
  '7_days',
  '15_days',
  '30_days',
  '45_days',
  '60_days',
  '90_days',
] as const;

const MAX_NAME_LENGTH = 128;

/**
 * The "details" + "context" fields a company (which is effectively a root
 * section) and a section present identically. Both form schemas are derived
 * from this: the company adds its relations, the section uses it as-is.
 */
export const sectionContextSchema = z.object({
  name: z
    .string()
    .trim()
    .nonempty(t('errors:forms.string.non_empty'))
    .max(MAX_NAME_LENGTH, t('errors:forms.string.max', { max: MAX_NAME_LENGTH })),
  external_id: idString(),
  integration_id: idString(),
  accounting_id: idString(),
  customer_group: z.string(),
  price_lists: z.array(z.string()),
  sales_agent_groups: z.array(z.string()),
  payment_on_account_enabled: z.boolean(),
  payment_on_account: z.object({
    credit_limit: optionalNumericString({ emptyAsUndefined: true, min: 0 }),
    balance: optionalNumericString({ emptyAsUndefined: true }),
    currency_code: z.union([
      z.literal(''),
      z
        .string()
        .trim()
        .regex(/^[A-Za-z]{3}$/, t('features:companies.pages.company_form_page.errors.currency')),
    ]),
    net_terms: z.union([z.enum(COMPANY_NET_TERMS), z.literal('')]),
  }),
});

export type SectionContextValues = z.input<typeof sectionContextSchema>;
export type SectionContextValidated = z.output<typeof sectionContextSchema>;
